import { Tooltip, Typography, useTheme } from "@mui/material";
import { scaleOrdinal } from "@visx/scale";
import { sumBy } from "lodash-es";
import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import type { DatasetMetric } from "@citrine/resources/metrics";

import * as sx from "./material-count-pie-chart.sx";

const CONSOLIDATION_FRACTION_THRESHOLD = 0.005;
const MIN_WEDGE_COUNT_BEFORE_CONSOLIDATON = 24;

interface Datum {
  id: string;
  index: number;
  label: string;
  other?: boolean;
  terminalMaterialCount: number;
}

const getArcDataTestid = (datum: Datum) =>
  datum.other
    ? "material-count-pie-chart-other-datasets-wedge"
    : "material-count-pie-chart-wedge";
const getArcKey = (datum: Datum) =>
  datum.other ? "other-datasets" : `dataset-${datum.id}`;
const hoveredContent = (props: Datum) => (
  <>
    <sx.TooltipDatasetName>{props.label}</sx.TooltipDatasetName>
    {": "}
    {props.terminalMaterialCount.toLocaleString()}
  </>
);
const pieSort = (data1: Datum, data2: Datum) => data1.index - data2.index;
const pieValue = (data: Datum) => data.terminalMaterialCount;

export function MaterialCountPieChart({
  colors,
  minWedgeCountBeforeConsolidation = MIN_WEDGE_COUNT_BEFORE_CONSOLIDATON,
  sortedDatasetCounts,
}: {
  colors: string[];
  minWedgeCountBeforeConsolidation?: number; // exposed as a prop for unit testing purposes
  sortedDatasetCounts: DatasetMetric[];
}) {
  const theme = useTheme();

  const { formatMessage } = useIntl();

  const totalMaterials = useMemo(
    () => sumBy(sortedDatasetCounts, "terminal_materials_count"),
    [sortedDatasetCounts],
  );

  const data = useMemo<Datum[]>(() => {
    const thresholdIndex = sortedDatasetCounts.findIndex(
      (dataset) =>
        dataset.terminal_materials_count / totalMaterials <
        CONSOLIDATION_FRACTION_THRESHOLD,
    );
    /**
     * If there are more than `minWedgeCountBeforeConsolidation` wedges, datasets
     * under the `CONSOLIDATION_FRACTION_THRESHOLD` will be consolidated into the
     * "Other Datasets" wedge.
     **/
    const consolidationIndex =
      thresholdIndex > -1
        ? Math.max(thresholdIndex, minWedgeCountBeforeConsolidation)
        : thresholdIndex;

    // If there is only 1 "other dataset", don't add an "other" section, just increase
    // the datasetsLimit by 1
    const otherDatasetsStartIndex =
      consolidationIndex === sortedDatasetCounts.length - 1 ||
      consolidationIndex === -1
        ? sortedDatasetCounts.length
        : consolidationIndex;

    const otherDatasets = sortedDatasetCounts.slice(otherDatasetsStartIndex);
    const otherDatasetsMaterialCount = sumBy(
      otherDatasets,
      (metric) => metric.terminal_materials_count,
    );

    return [
      ...sortedDatasetCounts
        .slice(0, otherDatasetsStartIndex)
        .map((metric, index) => ({
          id: metric.dataset_summary.id,
          index,
          label: metric.dataset_summary.name,
          terminalMaterialCount: metric.terminal_materials_count,
        })),
      otherDatasetsMaterialCount
        ? {
            id: "other",
            index: otherDatasetsStartIndex,
            label: formatMessage(
              {
                id: "resources.dataset.material-count-plot.other-datasets-count",
              },
              {
                datasetCount: otherDatasets.length.toLocaleString(),
              },
            ),
            other: true,
            terminalMaterialCount: sumBy(
              otherDatasets,
              (metric) => metric.terminal_materials_count,
            ),
          }
        : undefined,
    ].filter(Boolean);
  }, [
    formatMessage,
    minWedgeCountBeforeConsolidation,
    sortedDatasetCounts,
    totalMaterials,
  ]);

  const getFill = useMemo(() => {
    const scale = scaleOrdinal({
      domain: data?.map(({ id }) => id),
      range: data?.map((_, index) => colors[index % colors.length]),
    });
    return (datum: Datum) =>
      datum.other ? theme.palette.grey[600] : scale(datum.id);
  }, [colors, data, theme.palette.grey]);

  return totalMaterials ? (
    <sx.PieChart<Datum>
      centerContent={
        <sx.CenterContent>
          <Tooltip
            title={
              <FormattedMessage
                id="terminal-material-count"
                values={{
                  materialCount: totalMaterials.toLocaleString(),
                }}
              />
            }
          >
            <Typography
              variant={
                // Decrease the font size for larger material counts to prevent text overflow
                totalMaterials < 1e6 ? "h1" : totalMaterials < 1e7 ? "h2" : "h3"
              }
              sx={{
                color: "text.primary",
                fontFamily: theme.typography.fontFamilyMono,
                fontWeight: theme.typography.fontWeightRegular,
                textAlign: "center",
              }}
            >
              {totalMaterials.toLocaleString()}
            </Typography>
          </Tooltip>
        </sx.CenterContent>
      }
      data={data}
      getArcDataTestid={getArcDataTestid}
      getArcKey={getArcKey}
      getFill={getFill}
      hoveredContent={hoveredContent}
      pieSort={pieSort}
      pieValue={pieValue}
    />
  ) : null;
}
